import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

export const useModels = (modelName, { order_by = null, endpoint = 'getAll', modelId = null } = {}) => {
    const [models, setModels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const isFetchingRef = useRef(false); // Prevent concurrent fetches
    const [pagination, setPagination] = useState({
        current_page: 1,
        page_size: 24,
        order_by,
        total_resources: 0,
        total_pages: 0,
        has_next: false,
        has_previous: false
    });

    const fetchModels = useCallback(async (params = {}) => {
        // Prevent concurrent fetches
        if (isFetchingRef.current) {
            return;
        }

        try {
            isFetchingRef.current = true;
            setLoading(true);
            setError(null);

            let response;

            // Check if the modelName has a structured API endpoint
            if (api[modelName] && api[modelName][endpoint]) {
                // Use the structured API endpoint (e.g., api.borrowings.getMine)
                // Prepare parameters for the API call
                const apiParams = {
                    page: params.page || 1,
                    page_size: params.page_size || 24,
                    ...(params.order_by && { order_by: params.order_by }),
                    // Add other params only if they have values
                    ...Object.keys(params).reduce((acc, key) => {
                        if (key !== 'page' && key !== 'page_size' && key !== 'order_by' &&
                            params[key] !== undefined && params[key] !== '') {
                            acc[key] = params[key];
                        }
                        return acc;
                    }, {})
                };

                // Handle resource-specific and location-specific endpoints that require modelId as first parameter
                const resourceSpecificEndpoints = ['getBorrowings', 'getConsumptions', 'getRefills'];
                const locationSpecificEndpoints = ['getBorrowings'];
                const modelSpecificEndpoints = [...resourceSpecificEndpoints, ...locationSpecificEndpoints];

                if (modelSpecificEndpoints.includes(endpoint) && modelId) {
                    response = await api[modelName][endpoint](modelId, apiParams);
                } else {
                    response = await api[modelName][endpoint](apiParams);
                }

                // Handle different response formats
                // Some endpoints return structured data with models/pagination
                // Others return data directly as an array
                if (Array.isArray(response.data)) {
                    // Direct array response (like getMine)
                    setModels(response.data);
                    setPagination({
                        current_page: 1,
                        page_size: response.data.length,
                        order_by,
                        total_resources: response.data.length,
                        total_pages: 1,
                        has_next: false,
                        has_previous: false
                    });
                } else {
                    // Structured response with models and pagination
                    setModels(response.data.models || []);
                    setPagination(response.data.pagination || {
                        current_page: 1,
                        page_size: 24,
                        order_by,
                        total_resources: 0,
                        total_pages: 0,
                        has_next: false,
                        has_previous: false
                    });
                }
            } else {
                // Fallback to the generic endpoint
                const queryParams = new URLSearchParams();

                // Add default params
                queryParams.append('page', params.page || 1);
                queryParams.append('page_size', params.page_size || 24);
                if (params.order_by) queryParams.append('order_by', params.order_by);

                // Add other params only if they have values
                Object.keys(params).forEach(key => {
                    if (key !== 'page' && key !== 'page_size' && key != 'order_by' && params[key] !== undefined && params[key] !== '') {
                        queryParams.append(key, params[key]);
                    }
                });

                response = await api.get(`/${modelName}?${queryParams.toString()}`);

                // Generic endpoints return structured data
                setModels(response.data.models || []);
                setPagination(response.data.pagination || {
                    current_page: 1,
                    page_size: 24,
                    order_by,
                    total_resources: 0,
                    total_pages: 0,
                    has_next: false,
                    has_previous: false
                });
            }
        } catch (err) {
            setError(err.message || `Failed to fetch ${modelName}`);
            setModels([]);
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    }, [modelName, endpoint]);

    const refetch = useCallback((params, modelId) => {
        return fetchModels(params, modelId);
    }, [fetchModels]);

    return {
        models,
        loading,
        error,
        pagination,
        refetch
    };
};